import {Injectable} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {filter, map, take, takeUntil} from 'rxjs/operators';
import {AuthenticationResult, EventMessage, EventType, InteractionStatus} from '@azure/msal-browser';
import {Disposable} from '../../shared/extensions/disposable';
import {BehaviorSubject, Observable} from 'rxjs';
import {UserProfile} from '../common/user-profile';
import {Authorize, authorizeMap} from '../common/authorize-map';

@Injectable({
    providedIn: 'root'
})
export class UserProfileService extends Disposable {
    private userName = '';
    private userProperName = '';
    private userRoles: string[] = [];
    private userProfileSubject = new BehaviorSubject<UserProfile>(null);
    private azureId;

    constructor(
        private readonly msalService: MsalService,
        private broadcastService: MsalBroadcastService,
    ) {
        super();
        this.broadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this.$destroyed)
            )
            .subscribe(() => {
                this.checkAndSetActiveAccount();
            });
    }

    private checkAndSetActiveAccount(): void {
        const activeAccount = this.msalService.instance.getActiveAccount();

        if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
            const accounts = this.msalService.instance.getAllAccounts();
            this.msalService.instance.setActiveAccount(accounts[0]);
        }
        this.setProfileFromAccount();
    }

    public init(): void {
        this.broadcastService.msalSubject$
            .pipe(
                takeUntil(this.$destroyed),
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                )
            )
            .subscribe((result: EventMessage) => {
                // Casting payload as AuthenticationResult to access account
                const payload = result.payload as AuthenticationResult;
                this.msalService.instance.setActiveAccount(payload.account);
                this.setProfileFromAccount();
            });
    }

    get currentUserName(): string {
        return this.userName;
    }

    get currentUserProperName(): string {
        return this.userProperName;
    }

    get currentUserRoles(): string[] {
        return this.userRoles;
    }

    get currentUserProfile$(): Observable<UserProfile> {
        return this.userProfileSubject.asObservable();
    }

    public hasRoles(roles: string[]): boolean {
        if (!roles?.length) {
            return true;
        }

        if (!this.userRoles || this.userRoles?.length < 1) {
            return false;
        }
        return roles.some(r => this.userRoles.includes(r));
    }

    private setProfileFromAccount(): void {
        this.userName = this.msalService.instance.getActiveAccount()?.username;
        this.userProperName = this.msalService.instance.getActiveAccount()?.name;
        this.userRoles = this.msalService.instance.getActiveAccount()?.idTokenClaims['roles'];
        this.azureId = this.msalService.instance.getActiveAccount()?.idTokenClaims['oid'];
        this.userProfileSubject.next({
            userName: this.userName,
            properName: this.userProperName,
            roles: this.userRoles,
            azureId: this.azureId
        });
    }

    public authorized$(roles: any[]): Observable<boolean> {
        return this.currentUserProfile$.pipe(
            filter(user => !!user && user.roles?.length > 0),
            map(() => this.hasRoles(roles)),
            take(1)
        );
    }

    public hasAuthorization(authorization: Authorize): boolean {
        const {roles: sideNavRoles} = authorizeMap[authorization];
        return this.hasRoles(sideNavRoles);
    }
}
